import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Phone, CheckCircle2 } from "lucide-react";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import api from "@/lib/api";
import { useSite, telLink, waLink } from "@/context/SiteContext";
import { useVehicles } from "@/hooks/usePublicData";

const TRIP_TYPES = ["One Way", "Round Trip", "Local", "Airport Transfer", "Outstation"];
const DEFAULT_TAXIS = ["Sedan", "SUV", "Premium SUV", "Tempo Traveller"];

const empty = {
  full_name: "", phone: "", pickup_location: "", drop_location: "",
  travel_date: "", pickup_time: "", passengers: "", taxi_type: "",
  trip_type: "One Way", message: "",
};

export default function BookingForm({ compact = false, defaults = {} }) {
  const s = useSite();
  const { data: vehicles = [] } = useVehicles();
  const [form, setForm] = useState({ ...empty, ...defaults });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const taxiOptions = vehicles.length ? vehicles.map((v) => v.name) : DEFAULT_TAXIS;

  useEffect(() => {
    if (!form.taxi_type && taxiOptions.length) setForm((f) => ({ ...f, taxi_type: taxiOptions[0] }));
  }, [taxiOptions]); // eslint-disable-line

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/bookings", form);
      setDone(true);
      toast.success("Booking request received! We will call you shortly.");
    } catch (err) {
      toast.error("Could not submit. Please call us at " + s.phone);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    const waText = `Hi, I just booked a ${form.taxi_type} taxi (${form.trip_type}) from ${form.pickup_location || "—"} to ${form.drop_location || "—"} on ${form.travel_date || "—"}. Name: ${form.full_name}.`;
    return (
      <div className="text-center py-8 px-4" data-testid="booking-confirmation">
        <CheckCircle2 className="w-16 h-16 text-whatsapp mx-auto mb-4" />
        <h3 className="text-2xl font-heading font-bold text-navy-heading mb-2">Thank you, {form.full_name.split(" ")[0]}!</h3>
        <p className="text-slate-600 mb-2 max-w-md mx-auto">Your taxi booking request has been received. Our team will contact you shortly to confirm.</p>
        <div className="flex flex-wrap justify-center gap-3">
          <a href={telLink(s)} className="inline-flex items-center gap-2 bg-navy text-white font-semibold px-6 py-3 rounded-full hover:bg-navy-700 transition-colors"><Phone className="w-4 h-4" /> Call {s.phone}</a>
          <a href={waLink(s, waText)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-whatsapp text-white font-semibold px-6 py-3 rounded-full hover:brightness-95 transition-all"><WhatsAppIcon className="w-4 h-4" /> Confirm on WhatsApp</a>
        </div>
        <button onClick={() => { setForm({ ...empty, taxi_type: taxiOptions[0] }); setDone(false); }} className="mt-6 text-sm text-brand font-medium hover:underline">Make another booking</button>
      </div>
    );
  }

  const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none transition-all bg-white";
  const labelCls = "block text-xs font-semibold text-navy-heading mb-1.5";

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="booking-form">
      <div>
        <label className={labelCls}>Trip Type</label>
        <div className="flex flex-wrap gap-2">
          {TRIP_TYPES.map((t) => (
            <button type="button" key={t} onClick={() => setForm((f) => ({ ...f, trip_type: t }))}
              data-testid={`trip-type-${t.toLowerCase().replace(/ /g, "-")}`}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all ${form.trip_type === t ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-slate-200 hover:border-brand"}`}>
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Full Name *</label>
          <input className={inputCls} value={form.full_name} onChange={set("full_name")} placeholder="Your name" data-testid="booking-name-input" />
        </div>
        <div>
          <label className={labelCls}>Phone Number *</label>
          <input className={inputCls} value={form.phone} onChange={set("phone")} placeholder="Mobile number" data-testid="booking-phone-input" />
        </div>
        <div>
          <label className={labelCls}>Pickup Location</label>
          <input className={inputCls} value={form.pickup_location} onChange={set("pickup_location")} placeholder="From (city / address)" data-testid="booking-pickup-input" />
        </div>
        <div>
          <label className={labelCls}>Drop Location</label>
          <input className={inputCls} value={form.drop_location} onChange={set("drop_location")} placeholder="To (city / address)" data-testid="booking-drop-input" />
        </div>
        <div>
          <label className={labelCls}>Travel Date</label>
          <input type="date" className={inputCls} value={form.travel_date} onChange={set("travel_date")} data-testid="booking-date-input" />
        </div>
        <div>
          <label className={labelCls}>Pickup Time</label>
          <input type="time" className={inputCls} value={form.pickup_time} onChange={set("pickup_time")} data-testid="booking-time-input" />
        </div>
        <div>
          <label className={labelCls}>Passengers</label>
          <input type="number" min="1" className={inputCls} value={form.passengers} onChange={set("passengers")} placeholder="No. of passengers" data-testid="booking-passengers-input" />
        </div>
        <div>
          <label className={labelCls}>Taxi Type</label>
          <select className={inputCls} value={form.taxi_type} onChange={set("taxi_type")} data-testid="booking-taxi-type-select">
            {taxiOptions.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
      </div>

      {!compact && (
        <div>
          <label className={labelCls}>Message</label>
          <textarea rows={3} className={inputCls} value={form.message} onChange={set("message")} placeholder="Any special requirements?" data-testid="booking-message-input" />
        </div>
      )}

      <button type="submit" disabled={loading} data-testid="quick-booking-submit"
        className="w-full flex items-center justify-center gap-2 bg-brand hover:bg-royal text-white font-semibold py-3.5 rounded-full transition-all active:scale-[0.98] shadow-lg shadow-brand/25 disabled:opacity-70">
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Phone className="w-5 h-5" />}
        {loading ? "Sending..." : "Book a Taxi"}
      </button>
      <p className="text-center text-xs text-slate-400">Or call us directly at <a href={telLink(s)} className="text-brand font-semibold">{s.phone}</a></p>
    </form>
  );
}
